import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Spinner, Badge } from "react-bootstrap";
import { FaUser, FaInfoCircle, FaCalendarAlt, FaMoneyBillWave, FaCreditCard } from "react-icons/fa";
import { fetchTalentPaymentsApi } from "../../../services/api";
import MetaTags from "../../../components/MetaTags";
import DataTable from "../../../components/DataTable";
import './TalentBillingPage.css';

const TalentBillingPage = () => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadPayments = useCallback(async () => {
        try {
            const response = await fetchTalentPaymentsApi();
            setPayments(response.data.data || []);
        } catch (error) { 
            console.error(error); 
        } finally { 
            setLoading(false); 
        }
    }, []);

    useEffect(() => { 
        loadPayments(); 
    }, [loadPayments]);

    const columns = useMemo(() => [
        {
            accessorKey: 'index',
            header: '#',
            cell: (info) => <span className="opacity-50 text-white">{info.row.index + 1}</span>,
            enableSorting: false,
        },
        {
            accessorKey: 'userName',
            header: 'User Name',
            cell: (info) => <span className="fw-bold text-white">{info.getValue() || 'N/A'}</span>,
        },
        {
            accessorKey: 'roleName',
            header: 'Role Name',
            cell: (info) => <span className="fw-bold text-white">{info.getValue() || 'Audition Payment'}</span>,
        },
        {
            accessorKey: 'paymentId',
            header: 'Payment ID',
            cell: (info) => <span className="text-info font-monospace small">{info.getValue()}</span>,
        },
        {
            accessorKey: 'paymentStatus',
            header: 'Status',
            cell: (info) => {
                const val = info.getValue() || 'PENDING';
                const isSuccess = val === 'SUCCESS' || val === 'BOOKED';
                return (
                    <span className={`pmp-status-tag ${isSuccess ? 'pmp-status-active' : 'pmp-status-expired'}`}>
                        {val}
                    </span>
                );
            },
        },
        {
            accessorKey: 'paymentMode',
            header: 'Payment Mode',
            cell: (info) => (
                <Badge bg="info" className="text-uppercase" style={{ fontSize: '10px' }}>
                    {info.getValue() || 'UPI'}
                </Badge>
            ),
        },
        {
            accessorKey: 'amount',
            header: 'Amount',
            cell: (info) => <span className="fw-bold text-success">₹{info.getValue()}</span>,
        },
        {
            accessorKey: 'date',
            header: 'Date',
            cell: (info) => (
                <span className="text-white opacity-75">
                    {info.getValue() ? new Date(info.getValue()).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : 'N/A'}
                </span>
            ),
        }
    ], []);

    const renderMobileCard = useCallback((item) => {
        const isSuccess = item.paymentStatus === 'SUCCESS' || item.paymentStatus === 'BOOKED';
        const statusClass = isSuccess ? 'pmp-status-active' : 'pmp-status-expired';
        const formattedDate = item.date ? new Date(item.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : 'N/A';

        return (
            <div key={item.paymentId || item.id} className="project-mobile-card mb-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                        <h6 className="project-card-title m-0 text-white fw-bold">{item.roleName || 'Audition Payment'}</h6>
                        <small className="text-warning fw-bold">{item.userName}</small>
                    </div>
                    <span className={`pmp-status-tag ${statusClass}`}>
                        {item.paymentStatus || 'PENDING'}
                    </span>
                </div>

                <div className="card-data-grid mt-2 mb-2">
                    <div className="data-item">
                        <FaUser className="text-danger" />
                        <span className="lbl">User:</span>
                        <span className="val text-truncate" style={{ maxWidth: '110px' }}>{item.userName}</span>
                    </div>
                    <div className="data-item">
                        <FaInfoCircle className="text-info" />
                        <span className="lbl">Mode:</span>
                        <span className="val text-truncate">{item.paymentMode || 'UPI'}</span>
                    </div>
                    <div className="data-item">
                        <FaCalendarAlt className="text-success" />
                        <span className="lbl">Date:</span>
                        <span className="val">{formattedDate}</span>
                    </div>
                    <div className="data-item">
                        <FaMoneyBillWave className="text-warning" />
                        <span className="lbl">Amount:</span>
                        <span className="val fw-bold text-success">₹{item.amount}</span>
                    </div>
                </div>

                <hr className="compact-hr my-2" />

                <div className="d-flex justify-content-between align-items-center my-0">
                    <div className="d-flex align-items-center gap-1">
                        <FaCreditCard className="text-info flex-shrink-0" style={{ fontSize: '11px' }} />
                        <span className="text-white-50 small fw-semibold" style={{ fontSize: '11px', lineHeight: '1' }}>ID:</span>
                        <span className="house-name-gold font-monospace" style={{ fontSize: '11px', lineHeight: '1' }}>{item.paymentId}</span>
                    </div>
                    <div className="d-flex align-items-center gap-1 text-muted" style={{ fontSize: '11px', lineHeight: '1' }}>
                        <FaCalendarAlt className="text-success flex-shrink-0" style={{ fontSize: '10px' }} />
                        <span style={{ fontSize: '11px', lineHeight: '1' }}>{formattedDate}</span>
                    </div>
                </div>
            </div>
        );
    }, []);

    if (loading) {
        return (
            <div className="text-center p-5">
                <Spinner animation="border" variant="danger" />
                <p className="text-white mt-2">Loading Payment History...</p>
            </div>
        );
    }

    return (
        <div className="talent-billing-container p-2 p-md-3 animate-fade-in">
            <MetaTags title="Billing History - BookMyAudition" />
            
            <div className="page-header mb-3">
                <h5 className="text-uppercase fw-bold text-white m-0">
                    Billing <span style={{ color: 'var(--primary-color)' }}>History</span>
                </h5>
                <small className="text-muted">View all your audition payments</small>
            </div>

            <DataTable 
                columns={columns} 
                data={payments} 
                renderMobileItem={renderMobileCard} 
                searchPlaceholder="Search role, payment ID, or user..." 
            />
        </div>
    );
};

export default TalentBillingPage;